import { forwardRef, useId, type InputHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

interface FieldShellProps {
  children: React.ReactNode;
  description?: string;
  error?: string;
  htmlFor: string;
  label: string;
}

function FieldShell({ children, description, error, htmlFor, label }: FieldShellProps) {
  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-ink" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {error ? (
        <p className="text-sm text-danger" id={`${htmlFor}-error`}>
          {error}
        </p>
      ) : null}
      {!error && description ? (
        <p className="text-xs leading-relaxed text-muted">{description}</p>
      ) : null}
    </div>
  );
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  description?: string;
  error?: string;
  label: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, description, error, id, label, ...props }, ref) => {
    const generated = useId();
    const inputId = id ?? generated;
    return (
      <FieldShell description={description} error={error} htmlFor={inputId} label={label}>
        <input
          ref={ref}
          aria-describedby={error ? `${inputId}-error` : undefined}
          aria-invalid={Boolean(error)}
          className={cn(
            'min-h-11 w-full rounded-xl border border-line bg-canvas px-3.5 text-sm text-ink placeholder:text-muted/65 focus:border-accent',
            error && 'border-danger',
            className
          )}
          id={inputId}
          {...props}
        />
      </FieldShell>
    );
  }
);
Input.displayName = 'Input';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  description?: string;
  error?: string;
  label: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, description, error, id, label, ...props }, ref) => {
    const generated = useId();
    const inputId = id ?? generated;
    return (
      <FieldShell description={description} error={error} htmlFor={inputId} label={label}>
        <textarea
          ref={ref}
          aria-describedby={error ? `${inputId}-error` : undefined}
          aria-invalid={Boolean(error)}
          className={cn(
            'min-h-28 w-full resize-y rounded-xl border border-line bg-canvas px-3.5 py-3 text-sm text-ink placeholder:text-muted/65 focus:border-accent',
            error && 'border-danger',
            className
          )}
          id={inputId}
          {...props}
        />
      </FieldShell>
    );
  }
);
Textarea.displayName = 'Textarea';
